import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RecordNav } from "../components/RecordNav";

const codeKey = (c) => c.code || c.name;
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, FloppyDisk, CaretDown, CaretRight, Trash, FileText, ListChecks, Note as NoteIcon, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { useAutoSave, AutoSaveIndicator } from "../lib/useAutoSave";
import { StatusChip, ConfirmDialog } from "../components/shared";
import { AddressFields } from "../components/AddressFields";
import { NotesPane } from "../components/NotesPane";
import { ContactsPane } from "../components/ContactsPane";
import { usePerms } from "../lib/perms";
import { ViewOnlyBadge } from "../components/Permission";
import { isMobileViewport } from "../components/LineItemText";
import { suggestCode, CURRENCIES } from "../lib/constants";
import { DocLanguageSelect } from "../components/DocLanguageSelect";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function Section({ title, children, grid = true, defaultOpen = true, mobileCollapsed = false }) {
  const [open, setOpen] = useState(defaultOpen && !(mobileCollapsed && isMobileViewport()));
  const key = title.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <div className="border border-border bg-card mb-4" data-testid={`csection-${key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-secondary/50 transition-colors" data-testid={`csection-toggle-${key}`}>
        {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
        <span className="font-bold text-sm tracking-tight">{title}</span>
      </button>
      {open && <div className={`px-4 pb-4 pt-1 ${grid ? "grid grid-cols-2 gap-3 md:gap-4" : ""}`}>{children}</div>}
    </div>
  );
}
function F({ label, children }) {
  return (<div><Label className="overline-label">{label}</Label><div className="mt-1">{children}</div></div>);
}
function Row({ label, value, mono, tabular, bold }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">{label}</span>
      <span className={`${mono ? "font-mono text-xs" : ""} ${tabular ? "tabular" : ""} ${bold ? "font-bold text-primary" : "font-medium"}`}>{value}</span>
    </div>
  );
}

const EMPTY = {
  code: "", name: "", email: "", phone: "", address: "", address2: "", city: "",
  state: "", postcode: "", country: "", currency: "", credit_limit: 0, balance: 0,
  ship_to_address: "", ship_to_address2: "", ship_to_city: "", ship_to_state: "", ship_to_postcode: "", ship_to_country: "",
  bill_to_address: "", bill_to_address2: "", bill_to_city: "", bill_to_state: "", bill_to_postcode: "", bill_to_country: "",
};

export default function CustomerCard() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: customer, isLoading } = useQuery({ queryKey: ["customer", id], queryFn: async () => (await api.get(`/customers/${id}`)).data, enabled: !isNew });
  const { data: salesDocs = [] } = useQuery({ queryKey: ["sales-orders"], queryFn: async () => (await api.get("/sales-orders")).data, enabled: !isNew });

  const [form, setForm] = useState(EMPTY);
  const [codeEdited, setCodeEdited] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  useEffect(() => { if (customer) { setForm({ ...EMPTY, ...customer }); setCodeEdited(true); } }, [customer]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const SHIP_FROM_BILL = [
    ["ship_to_address", "bill_to_address"], ["ship_to_address2", "bill_to_address2"],
    ["ship_to_city", "bill_to_city"], ["ship_to_state", "bill_to_state"],
    ["ship_to_postcode", "bill_to_postcode"], ["ship_to_country", "bill_to_country"],
  ];
  const copyBillingToShipping = (f) => {
    const next = { ...f };
    SHIP_FROM_BILL.forEach(([s, b]) => { next[s] = f[b] || ""; });
    return next;
  };
  const toggleSameAsBilling = (checked) => {
    setSameAsBilling(checked);
    if (checked) setForm((f) => copyBillingToShipping(f));
  };
  // keep shipping synced while the box is ticked and billing changes
  useEffect(() => {
    if (sameAsBilling) setForm((f) => copyBillingToShipping(f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsBilling, form.bill_to_address, form.bill_to_address2, form.bill_to_city, form.bill_to_state, form.bill_to_postcode, form.bill_to_country]);
  const onName = (val) => setForm((f) => ({ ...f, name: val, ...(!codeEdited && isNew ? { code: suggestCode(val) } : {}) }));

  const available = Number(form.credit_limit || 0) - Number(form.balance || 0);
  const docs = salesDocs.filter((d) => d.customer_id === id).slice(0, 10);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["customers"] }); qc.invalidateQueries({ queryKey: ["customer", id] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const payload = () => ({
    code: form.code, name: form.name, email: form.email, phone: form.phone,
    address: form.address, address2: form.address2, city: form.city, state: form.state,
    postcode: form.postcode, country: form.country, credit_limit: Number(form.credit_limit) || 0,
    currency: form.currency || "", language: form.language || "en",
    ship_to_address: form.ship_to_address, ship_to_address2: form.ship_to_address2, ship_to_city: form.ship_to_city,
    ship_to_state: form.ship_to_state, ship_to_postcode: form.ship_to_postcode, ship_to_country: form.ship_to_country,
    bill_to_address: form.bill_to_address, bill_to_address2: form.bill_to_address2, bill_to_city: form.bill_to_city,
    bill_to_state: form.bill_to_state, bill_to_postcode: form.bill_to_postcode, bill_to_country: form.bill_to_country,
  });
  const save = useMutation({
    mutationFn: async () => isNew ? (await api.post("/customers", payload())).data : (await api.put(`/customers/${id}`, payload())).data,
    onSuccess: (res) => { invalidate(); toast.success(isNew ? "Customer created" : "Customer saved"); if (isNew) navigate(`/sales/customer/${res.id}`, { replace: true }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const [delOpen, setDelOpen] = useState(false);
  const del = useMutation({ mutationFn: async () => api.delete(`/customers/${id}`), onSuccess: () => { invalidate(); toast.success("Customer deleted"); navigate("/sales?tab=customers"); } });

  const createdRef = useRef(false);
  const autoSaveFn = async () => {
    if (isNew) {
      if (createdRef.current) return;
      createdRef.current = true;
      try { const res = (await api.post("/customers", payload())).data; invalidate(); navigate(`/sales/customer/${res.id}`, { replace: true }); return res; }
      catch (e) { createdRef.current = false; throw e; }
    }
    const res = (await api.put(`/customers/${id}`, payload())).data; invalidate(); return res;
  };
  const { can } = usePerms();
  const viewOnly = !can("sales.edit");
  const { status: autoStatus, flush: autoSaveFlush } = useAutoSave({ active: !viewOnly, canSave: !!form.name, createMode: isNew, snapshot: payload(), save: autoSaveFn });
  const statement = useMutation({
    mutationFn: async () => (await api.post(`/customers/${id}/statement-email`)).data,
    onSuccess: (r) => toast.success(`Statement emailed to ${r.sent_to} · ${r.total ? `$${Number(r.total).toLocaleString()}` : ""} outstanding`),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (!isNew && isLoading) return <div className="flex items-center justify-center py-24"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-up pb-10" data-testid="customer-card" onBlur={autoSaveFlush}>
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => navigate("/sales?tab=customers")} className="p-1.5 -ml-1.5 text-muted-foreground hover:text-primary" data-testid="customer-card-back"><ArrowLeft size={18} weight="bold" /></button>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="customer-card-title">{isNew ? "New Customer" : `${customer.code} · ${customer.name}`}</h1>
        {!isNew && <StatusChip status={available < 0 ? "Overdue" : "Open"} />}
        <ViewOnlyBadge show={viewOnly && !isNew} />
        {!isNew && <div className="ml-auto"><RecordNav source="customers" currentId={id} sortKey={codeKey} basePath="/sales/customer" testId="customer-nav" /></div>}
      </div>

      <div className="flex items-center gap-1 flex-wrap border border-border bg-card px-2 py-1.5 mb-5 rounded-sm">
        {!viewOnly && <button onMouseDown={(e) => e.preventDefault()} onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="customer-ribbon-save" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"><FloppyDisk size={15} weight="bold" /> {isNew ? "Create" : "Save"}</button>}
        {!viewOnly && <AutoSaveIndicator status={autoStatus} isNew={isNew} testid="customer-autosave" />}
        {!isNew && can("sales.delete") && <button onClick={() => setDelOpen(true)} data-testid="customer-ribbon-delete" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-foreground hover:bg-secondary"><Trash size={15} weight="bold" /> Delete</button>}
        {!isNew && can("sales.post") && <button onClick={() => statement.mutate()} disabled={statement.isPending} data-testid="customer-ribbon-statement" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-40"><EnvelopeSimple size={15} weight="bold" /> {statement.isPending ? "Sending…" : "Email Statement"}</button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <fieldset disabled={viewOnly} className="lg:col-span-2 min-w-0" data-testid="customer-form-fields">
          <Section title="General">
            <F label="Code"><Input data-testid="customer-code" value={form.code || ""} onChange={(e) => { setCodeEdited(true); setF("code", e.target.value.toUpperCase()); }} placeholder="Auto" className="rounded-sm font-mono" /></F>
            <F label="Name"><Input data-testid="customer-name" value={form.name} onChange={(e) => onName(e.target.value)} className="rounded-sm" /></F>
            <F label="Currency">
              <Select value={form.currency || "none"} onValueChange={(v) => setF("currency", v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="customer-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Document language">
              <DocLanguageSelect value={form.language} onChange={(v) => setF("language", v)} testId="customer-language" />
            </F>
          </Section>

          <Section title="Address & Contact" grid={false}>
            <AddressFields values={form} onChange={(f, val) => setF(f, val)} testidPrefix="customer" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label className="overline-label">Email</Label><Input data-testid="customer-email" value={form.email} onChange={(e) => setF("email", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="overline-label">Phone</Label><Input data-testid="customer-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="mt-1 rounded-sm" /></div>
            </div>
          </Section>

          <Section title="Contacts" grid={false}>
            <ContactsPane basePath={`/customers/${id}`} isNew={isNew} readOnly={viewOnly} contacts={customer?.contacts || []} testidPrefix="customer" onChanged={() => qc.invalidateQueries({ queryKey: ["customer", id] })} />
          </Section>

          <Section title="Ship-to Address" grid={false} mobileCollapsed>
            <p className="text-xs text-muted-foreground mb-2">Default delivery address — prefills new sales orders. Leave blank to use the main address above.</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-3 select-none" data-testid="customer-same-as-billing-label">
              <input type="checkbox" data-testid="customer-same-as-billing" checked={sameAsBilling} onChange={(e) => toggleSameAsBilling(e.target.checked)} className="accent-primary h-4 w-4" />
              <span>Same as billing address</span>
            </label>
            <div className={sameAsBilling ? "opacity-60" : ""}>
              <AddressFields values={form} onChange={(f, val) => setF(f, val)} prefix="ship_to_" testidPrefix="customer-ship" disabled={sameAsBilling} />
            </div>
          </Section>

          <Section title="Billing Address" grid={false} mobileCollapsed>
            <p className="text-xs text-muted-foreground mb-2">Default invoice address — prefills new sales orders. Leave blank to use the main address above.</p>
            <AddressFields values={form} onChange={(f, val) => setF(f, val)} prefix="bill_to_" testidPrefix="customer-bill" />
          </Section>

          <Section title="Invoicing">
            <F label="Credit Limit"><Input type="number" step="0.01" data-testid="customer-credit-limit" value={form.credit_limit} onChange={(e) => setF("credit_limit", e.target.value)} className="rounded-sm num" /></F>
            <F label="Balance (LCY)"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular" data-testid="customer-balance">{money(form.balance)}</div></F>
            <F label="Available Credit"><div className={`h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular ${available < 0 ? "text-red-600 font-semibold" : ""}`} data-testid="customer-available-credit">{money(available)}</div></F>
          </Section>
        </fieldset>

        <div className="lg:col-span-1">
          <div className="border border-border bg-card sticky top-4" data-testid="customer-info-pane">
            <Tabs defaultValue="details">
              <TabsList className="w-full rounded-none border-b border-border bg-secondary/40 h-auto p-0">
                <TabsTrigger value="details" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><ListChecks size={14} className="mr-1" /> Details</TabsTrigger>
                <TabsTrigger value="documents" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><FileText size={14} className="mr-1" /> Documents</TabsTrigger>
                <TabsTrigger value="notes" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><NoteIcon size={14} className="mr-1" /> Notes</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="p-4 space-y-2.5 text-sm mt-0">
                <Row label="Code" value={isNew ? "—" : form.code} mono />
                <Row label="Country" value={form.country || "—"} />
                <Row label="Currency" value={form.currency || "—"} />
                <div className="border-t border-border pt-2.5 space-y-2.5">
                  <Row label="Balance" value={money(form.balance)} tabular />
                  <Row label="Credit Limit" value={money(form.credit_limit)} tabular />
                  <Row label="Available" value={money(available)} tabular bold />
                </div>
              </TabsContent>
              <TabsContent value="documents" className="p-4 mt-0">
                {isNew ? (
                  <p className="text-sm text-muted-foreground">Save the customer to see their documents.</p>
                ) : docs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sales documents yet.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="customer-documents-list">
                    {docs.map((d) => (
                      <button key={d.id} onClick={() => navigate(`/sales/doc/${d.id}?tab=customers`)} data-testid="customer-doc-link" className="w-full flex items-center justify-between gap-2 border border-border rounded-sm px-2.5 py-2 text-left hover:bg-secondary/60 transition-colors">
                        <div className="min-w-0">
                          <p className="font-mono text-xs font-medium truncate">{d.doc_no}</p>
                          <p className="text-[11px] text-muted-foreground">{d.date}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusChip status={d.status} />
                          <span className="tabular text-xs font-medium">{money(d.total)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="notes" className="p-4 mt-0">
                <NotesPane basePath={`/customers/${id}`} isNew={isNew} readOnly={viewOnly} notes={customer?.notes || []} testidPrefix="customer" onChanged={() => qc.invalidateQueries({ queryKey: ["customer", id] })} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <ConfirmDialog open={delOpen} onOpenChange={setDelOpen} title="Delete this customer?" description={`${form.name} will be permanently deleted.`} confirmLabel="Delete" testid="customer-card-delete-confirm" onConfirm={() => del.mutate()} />
    </div>
  );
}
